import { CardHeader, Drawer, useMediaQuery, Box, Button, Card, CardContent, FormHelperText,
  Grid, MenuItem, Stack, Typography} from "@mui/material";
import { useGetList, useGetMainList, useRefresh, useSaveContext } from "ra-core"
import { FC, useCallback, useMemo, useRef } from "react";
import { useFormik, useFormikContext } from "formik";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import TextField from "@/components/formik/TextField";
import { RecPatch } from "@/dataProvider/tools";
import { CreateDatabaseInput, DatabaseType, EngineResponseDto, EnumOptionResponseDto } from "@/gen/api/backup";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { genUrlWithHashid } from "@/utils/hashid";
import SelectField from "../formik/SelectField";
import Create from "@/controller/Create";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { EncryptionKeyResponseDto } from "@/gen/api/encrypt";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import retrieveAxiosErr from "@/utils/retrieveAxiosErr";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { KEY_MANAGEMENT } from "@/constants";
import { nonSqlDbTypes } from "../obfuscation/defs";


export interface CreateBackupProps {
  open: boolean;
  setOpen: (x:boolean) => void;
}

const resource = "databases"

const CreateBackup: FC<CreateBackupProps> = ({ open, setOpen, ...props}) => {

  const refresh = useRefresh()
  const handlers = useDefaultSavedHandlers('Create Backup Job')
  handlers.onSuccess.merge(useCallback((resp: { data: RecPatch }) => {
    setOpen(false)
    refresh()
  }, [setOpen, refresh]))

  const { data: dbTypesMap } = useGetList<EnumOptionResponseDto & RecPatch>('database-types')
  const dbTypes = Object.values(dbTypesMap || {}).sort((a, b) => a.id - b.id)

  return (
    <Create resource={resource} basePath={""} {...handlers} >
      <CreateBackupBody open={open} setOpen={setOpen} dbTypes={dbTypes} />
    </Create>
  )
}

export interface CreateBackupBodyProps extends CreateBackupProps {
  dbTypes: (EnumOptionResponseDto & RecPatch)[];
}

const validationSchema =
  Yup.object().shape({
    name: Yup.string().max(255).required('Database Name is required'),
    databaseType: Yup.number().required('Database Engine is required'),
    engineId: Yup.number().required('Engine Version is required'),
    encryptionKeyName: Yup.string().max(255, 'Encryption Key Name must be at most 255 characters'),
    description: Yup.string().max(255),
  })

const createNewKeyStub = {} as any

export const CreateBackupBody: FC<CreateBackupBodyProps> = ({
  open,
  setOpen,
  dbTypes,
}) => {
  const hasKeyPermit = useHasPermit([KEY_MANAGEMENT])

  const save = useSimpleSaveAction()
  const handleClose = (): void => {
    setOpen(false);
  };

  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const initialValues = useMemo(() => ({
    submit: '',
    name: '',
    databaseType: '' as string,
    engineId: '' as string,
    encryptionKeyId: '' as '' | number,
    encryptionKeyName: '',
    description: '',
  }), [])

  const validate = (values: typeof initialValues) => {
    const errors = {} as any
    if (!values.encryptionKeyId) {
      errors['encryptionKey'] = 'Encryption Key is required'
    } else if (values.encryptionKeyId === createNewKeyStub) {
      if (!values.encryptionKeyName) {
        errors['encryptionKeyName'] = 'Encryption Key Name is required'
      } else {
        errors['other'] = 'Press "Create Key" to create new key first'
      }

    }
    if (Object.keys(errors).length > 0) {
      return errors
    }
  }
  const dispatch = useDispatch()
  const onSubmit: OnSubmit<typeof initialValues> = (values, {
    setStatus,
    setErrors,
    setSubmitting,
    resetForm,
  }) => {
    const options = {
      onSuccess: (resp: { data: RecPatch }) => {
        resetForm({ 
          values: initialValues,
          isSubmitting: false,
          status: { success: true }
        })
        if (resp?.data?.id) {
          const databaseType = +(values.databaseType) as DatabaseType
          const detailUrl =
            nonSqlDbTypes.has(databaseType)
              ? `/databases/$0/show`
              : `/backups-n-validations/list/$0/show`

          dispatch(push(
            genUrlWithHashid(detailUrl, resp.data.id, values.name)))
        }
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    const payload: CreateDatabaseInput = {
      ...values,
      engineId: +(values.engineId || 0),
      encryptionKeyId: values.encryptionKeyId || undefined,
      ...({
        encryptionKeyName: undefined,
      } as any),
    }
    save(payload, options)
  }

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema, validate })
  const { errors, isSubmitting, values, setFieldValue, resetForm } = formikCtrl

  const { data: enginesMap } = useGetList<EngineResponseDto & RecPatch>(
    values.databaseType ? 'database-engine-versions' : 'noop', undefined, undefined, { databaseType: +(values.databaseType || '') })
  const engines = Object.values(enginesMap || {}).sort((a, b) => {
    return a.version! > b.version! ? -1
      : a.version! === b.version!  ? (a.id - b.id)
      : 1
  })
  
  const { data: encryptKeyData, refetch: refetchKeys } = useGetMainList<EncryptionKeyResponseDto&RecPatch>('settings/encryption/keys', pages(), sorts(), {}, { action: 'CUSTOM_DATA_QUERY' })
  const ecnryptKeys = Object.values(encryptKeyData || {}).sort((a, b) => a.id - b.id)

  const keySelectRef = useRef<HTMLDivElement>(null)
  const createKeyHandlers = useDefaultSavedHandlers('Create Encryption Key')
  createKeyHandlers.onSuccess.merge(useCallback(() => {
    keySelectRef.current?.querySelector('input')?.focus()
    refetchKeys()
  }, [refetchKeys]))

  useEffect(() => {
    setFieldValue('engineId', '')
  }, [engines.map(a => a.id).join(','), setFieldValue]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open, resetForm])


  return (
    <Drawer
      anchor="right"
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >
      <Card 
        sx={{ 
          overflowY: 'auto',
          mb: 4,
        }}
      >
        <CardHeader title="Create Backup Job" />
        <CardContent>
          <OdForm formikCtrl={formikCtrl}>

            <Grid
              container
              spacing={4}
            >
              <Grid item xs={12} >
                <Typography color="textCaption" variant="body2">
                By creating a backup job, you can import a new database to the OnData Platform.
                </Typography>
              </Grid>


              <Grid item xs={12} >
                <TextField label="Database Name*" name="name" />
              </Grid>
              <Grid item xs={12} >
                <TextField 
                  disabled={!dbTypes || dbTypes.length === 0}
                  label="Database Engine*"
                  name="databaseType"
                  select
                  variant="outlined"
                >
                  {dbTypes.map(t => (
                    <MenuItem key={t.id} value={t.id + ''}>{t.text}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} >
                <TextField 
                  disabled={!engines || engines.length === 0}
                  label="Engine Version*"
                  name="engineId"
                  select
                  variant="outlined"
                >
                  {engines.map(e => (
                    <MenuItem key={e.id} value={e.id + ''}>{e.version + (e.edition ? ' ' + e.edition : '')}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} >
                <SelectField
                  label="Encryption Key*"
                  name="encryptionKeyId"
                  ref={keySelectRef}
                >
                  {ecnryptKeys.map(k => 
                    <MenuItem key={k.id} value={k.id as any} >
                      <Typography variant="body1" sx={{ 
                        maxWidth: 480,
                        wordBreak: 'keep-all',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {k.name}
                      </Typography>
                    </MenuItem>
                  )}
                  {hasKeyPermit &&
                    <MenuItem value={createNewKeyStub}>
                      <Typography variant="body2" color="InactiveCaptionText">
                        [ create new key ]
                      </Typography>
                    </MenuItem>}
                </SelectField>
                {ecnryptKeys.length === 0 && !hasKeyPermit &&
                  <FormHelperText sx={{ ml: 2 }} >Please contact your SystemAdmin or SecurityAdmin to help prepare the Encryption Keys for you.</FormHelperText>}
              </Grid>
              {hasKeyPermit && values.encryptionKeyId === createNewKeyStub &&
                <Grid item xs={12} sx={{ display: 'flex', alignItems: 'flex-start' }} >
                  <Box>
                    <TextField 
                      label="Encryption Key Name*" 
                      name="encryptionKeyName" 
                      fullWidth={false} 
                      sx={{ width: 332, mr: 2 }} 
                    />
                    {!errors.encryptionKeyName &&
                      <FormHelperText >Press "Create Key" to confirm input.</FormHelperText>}
                  </Box>
                  <Create resource="settings/encryption/keys" basePath="" {...createKeyHandlers} >
                    <CreateEncryptKey />
                  </Create>
                </Grid>
              }
              <Grid item xs={12} >
                <TextField label="Description" name="description" />
              </Grid>
            </Grid>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                py: 2,
                mt: 2,
              }}
            >
              <Button
                color="primary"
                disabled={isSubmitting}
                variant="outlined"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={Object.keys(errors).some(s => s !== 'submit') || isSubmitting}
                type="submit"
                variant="contained"
              >
                Create Backup Job
              </Button>
            </Stack>

          </OdForm>

        </CardContent>
      </Card>
    </Drawer>
  )
}

export default CreateBackup;


const CreateEncryptKey: FC = () => {
  const save = useSimpleSaveAction()
  const { saving } = useSaveContext()
  const { values: { encryptionKeyName }, errors, setFieldValue, setFieldError } = useFormikContext<{encryptionKeyName:string}>()

  const clickHandler = () => {
    if (!encryptionKeyName) { return }
    save({ name: encryptionKeyName }, {
      onSuccess: (resp: { data: any }) => {
        setFieldValue('encryptionKeyId', resp.data.id)
        setFieldValue('encryptionKeyName', '')
      },
      onFailure: (err:any) => {
        setFieldError('submit', retrieveAxiosErr(err))
      }
    })
  }
  return (
    <Button 
      disabled={!!errors['encryptionKeyName'] || saving}
      variant="contained" 
      color="primary" 
      onClick={clickHandler}
    >
      Create Key
    </Button>
  )
}

